"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";
import ToggleTheme from "../Toggle/ToggleTheme";

const MOBILE_NAV_ITEMS = [
  {
    id: 0,
    navTitle: "home",
  },
  {
    id: 1,
    navTitle: "showreel",
  },
  {
    id: 2,
    navTitle: "services",
  },
  {
    id: 3,
    navTitle: "about",
  },
  {
    id: 4,
    navTitle: "contact",
  },
];

export default function NavMobile() {
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  const hideNavItemsVariant = {
    opened: {
      opacity: 0,
      y: "-100%",
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
    closed: {
      opacity: 1,
      y: "0%",
      transition: {
        delay: 1.1,
        duration: 0.5,
        ease: "easeInOut",
      },
    },
  };

  const mobileMenuVariant = {
    opened: {
      y: "0%",
      transition: {
        delay: 0.15,
        duration: 1.1,
        ease: [0.74, 0, 0.19, 1.02],
      },
    },
    closed: {
      y: "-100%",
      transition: {
        delay: 0.35,
        duration: 0.63,
        ease: [0.74, 0, 0.19, 1.02],
      },
    },
  };

  const fadeInVariant = {
    opened: {
      opacity: 1,
      transition: {
        delay: 1.2,
      },
    },
    closed: { opacity: 0 },
  };

  const ulVariant = {
    opened: {
      transition: {
        delayChildren: 1,
        staggerChildren: 0.18,
      },
    },
    closed: {
      transition: {
        staggerChildren: 0.06,
        staggerDirection: -1,
      },
    },
  };

  const liVariant = {
    opened: {
      opacity: 1,
      y: "0%",
      transition: {
        duration: 0.65,
        ease: "easeOut",
      },
    },
    closed: {
      opacity: 0,
      y: "100%",
      transition: {
        duration: 0.25,
        ease: "easeInOut",
      },
    },
  };

  const fadeInStart = { opacity: 0 };
  const fadeInEnd = { opacity: 1 };
  const fadeInTransition = { duration: 1 };

  return (
    <motion.nav
      className="flex justify-between p-4 md:px-[45px] md:py-[35px] bg-transparent absolute z-50 w-full"
      initial="closed"
      animate={mobileNavOpen ? "opened" : "closed"}
    >
      <div className="overflow-y-hidden">
        <motion.div
          initial={fadeInStart}
          animate={fadeInEnd}
          transition={fadeInTransition}
          className="w-[60px] h-[60px]"
          variants={hideNavItemsVariant}
        >
          <Image
            alt="Logo Ho Van Doan"
            src="/images/logo.svg"
            width={0}
            height={0}
            sizes="100vw"
            quality={100}
            className="w-full h-auto object-fill"
          />
        </motion.div>
      </div>
      <motion.div
        className="overflow-y-hidden"
        initial={fadeInStart}
        animate={fadeInEnd}
        transition={fadeInTransition}
      >
        <motion.div
          className="uppercase text-[13px] hover:cursor-pointer flex items-center gap-2"
          variants={hideNavItemsVariant}
        >
          <ToggleTheme />
          <div onClick={() => setMobileNavOpen(true)}>
            <Image
              alt="Menu Icon"
              src="/icons/menu.svg"
              quality={100}
              height={40}
              width={40}
            />
          </div>
        </motion.div>
      </motion.div>
      <motion.div
        variants={mobileMenuVariant}
        className="mobile-menu fixed z-50 h-screen w-full flex flex-col items-center bg-[#ffffffd7] dark:bg-[#000000d7] left-0 top-0 px-[45px] py-[30px]"
      >
        <div className="flex w-full justify-between items-center">
          <motion.div
            className="self-start cursor-pointer w-[60px] h-[60px]"
            variants={fadeInVariant}
          >
            <Image
              alt="Logo Ho Van Doan"
              src="/images/logo.svg"
              width={0}
              height={0}
              sizes="100vw"
              quality={100}
              className="w-full h-auto object-fill"
            />
          </motion.div>
          <motion.div
            className="self-end cursor-pointer"
            variants={fadeInVariant}
            onClick={() => setMobileNavOpen(false)}
          >
            <Image
              alt="Menu Icon"
              src="/icons/close.svg"
              quality={100}
              height={50}
              width={50}
            />
          </motion.div>
        </div>
        <div className="w-full md:flex items-center justify-center gap-0 md:gap-4 md:mt-8">
          <div className="w-full md:w-1/2 flex justify-center md:justify-end">
            <motion.ul
              variants={ulVariant}
              className="mt-6 md:mt-0 list-none w-[345px] "
            >
              {MOBILE_NAV_ITEMS.map((navItem) => (
                <motion.li
                  whileTap={{ scale: 0.95 }}
                  key={navItem.id}
                  className="overflow-y-hidden select-none mx-0 my-2 lg:my-5 hover:cursor-pointer"
                >
                  <motion.div
                    className="text-center md:text-start capitalize text-[28px] md:text-[34px] font-semibold dark:text-main-gray hover:text-main-blue dark:hover:text-main-blue"
                    variants={liVariant}
                  >
                    {navItem.navTitle}
                  </motion.div>
                </motion.li>
              ))}
            </motion.ul>
          </div>

          <motion.div
            variants={fadeInVariant}
            className="w-full md:w-1/2 mt-2 md:mt-0"
          >
            <div className="overflow-hidden rounded-lg relative md:w-[345px] md:h-[345px] group cursor-pointer flex justify-center">
              <Image
                alt="Banner"
                src="/images/avatar/hodoan-banner.jpg"
                className="rounded-lg transition-transform duration-300 group-hover:scale-[1.1] object-cover w-[300px] h-[300px] md:w-[345px] md:h-[345px]"
                width={0}
                height={0}
                sizes="100vw"
              />
              <div className="absolute top-0 left-0 bg-gradient-to-t from-[#0007] to-[#0000] w-full h-full">
                <span className="text-sm absolute right-8 top-2 md:top-4 md:right-4 font-medium px-5 py-2 rounded-full bg-white text-main-dark">
                  My Self
                </span>
                <h3 className="font-semibold text-2xl md:text-3xl  absolute bottom-2 left-6 md:bottom-4 md:left-4 text-main-gray">
                  Hồ Văn Đoan
                </h3>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.nav>
  );
}
